import {
    Card,
    Text,
    Image,
    Badge,
    Group,
    Stack,
    ActionIcon,
    Tooltip,
} from '@mantine/core'
import { Link, useNavigate } from 'react-router-dom'
import { formatPrice } from '../utils/format'
import type { GamePlatform } from '../types'
import { useDarkMode } from '@/hooks/useDarkMode'
import { useAuth } from '@/hooks/useAuth'
import { useState } from 'react'
import { IconPhoto, IconPlus, IconEdit } from '@tabler/icons-react'
import { ImageBrowserModal } from './ImageBrowserModal'
import { AddPriceModal } from './AddPriceModal'
import { useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { gamesApi } from '../services/api'
import { searchOnAmazon } from '../utils/amazon-search'

interface GameCardProps {
    gamePlatform: GamePlatform
    onClick?: () => void
    showDescription?: boolean
    compact?: boolean
}

// Helper function to get rating badge color based on Metacritic score
const getRatingColor = (score: number): string => {
    if (score >= 90) return 'green'      // Universal acclaim
    if (score >= 80) return 'blue'       // Generally favorable reviews
    if (score >= 70) return 'yellow'     // Generally favorable reviews
    if (score >= 60) return 'orange'     // Mixed or average reviews
    return 'red'                         // Generally unfavorable reviews
}

// Helper function to check if release date is in the future
const isFutureRelease = (releaseDate?: string): boolean => {
    if (!releaseDate) return false
    const release = new Date(releaseDate)
    const now = new Date()
    return release > now
}

// Helper function to check if game was released in the last 7 days
const isRecentRelease = (releaseDate?: string): boolean => {
    if (!releaseDate) return false
    const release = new Date(releaseDate)
    const now = new Date()
    const diffTime = now.getTime() - release.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays >= 0 && diffDays <= 7
}

// Helper function to format release date for display
const formatReleaseDate = (releaseDate: string): string => {
    const date = new Date(releaseDate)
    const now = new Date()
    const diffTime = date.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Tomorrow'
    if (diffDays === -1) return 'Yesterday'
    if (diffDays > 0 && diffDays <= 7) return `${diffDays} days`
    if (diffDays < 0 && diffDays >= -7) return `${Math.abs(diffDays)} days ago`
    // For more than 7 days in the future or more than 7 days in the past, show the full localized date
    return date.toLocaleDateString(navigator.language, { month: 'short', day: 'numeric', year: 'numeric' })
}

export const GameCard: React.FC<GameCardProps> = ({
    gamePlatform,
    onClick,
    showDescription = false,
    compact = false
}) => {
    const { colorScheme } = useDarkMode()
    const { isAdmin } = useAuth()
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const imageUrl = gamePlatform.imageUrl
    const lowestPrice = gamePlatform.lowestPrice

    // Admin modal states
    const [imageBrowserOpened, setImageBrowserOpened] = useState(false)
    const [addPriceModalOpened, setAddPriceModalOpened] = useState(false)

    // Check if release date is in the future or recent (last 7 days)
    const isFuture = isFutureRelease(gamePlatform.releaseDate)
    const isRecent = isRecentRelease(gamePlatform.releaseDate)
    const shouldShowReleaseDate = isFuture || isRecent

    // Convert string values to numbers for calculations
    const currentPrice = lowestPrice?.amount ? Number(lowestPrice.amount) : null;
    const priceDrop = lowestPrice?.priceDrop ? Number(lowestPrice.priceDrop) : 0;
    const percentageChange = lowestPrice?.percentageChange ? Number(lowestPrice.percentageChange) : 0;

    const hasPriceDrop = priceDrop > 0 && Math.abs(percentageChange) > 1;

    const oldPrice = hasPriceDrop && currentPrice && priceDrop > 0
        ? currentPrice + priceDrop
        : null;
    const dropPercentage = Math.abs(percentageChange);

    // Admin handlers
    const handleOpenImageBrowser = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setImageBrowserOpened(true)
    }

    const handleCloseImageBrowser = () => {
        setImageBrowserOpened(false)
    }

    const handleImageSelect = async (imageUrl: string) => {
        try {
            // Update the game platform image
            await gamesApi.updateGamePlatform(gamePlatform.gameId, gamePlatform.platformId, {
                imageUrl: imageUrl
            })

            // Invalidate relevant queries to refresh the data
            // Target specific game queries with country parameter
            queryClient.invalidateQueries({
                queryKey: ['game', gamePlatform.game.slug, gamePlatform.platform.slug],
                exact: false
            })
            queryClient.invalidateQueries({
                queryKey: ['game-platforms', gamePlatform.game.slug],
                exact: false
            })

            // Invalidate search queries
            queryClient.invalidateQueries({
                queryKey: ['search'],
                exact: false
            })

            // Invalidate filtered game platforms queries
            queryClient.invalidateQueries({
                queryKey: ['gamePlatforms'],
                exact: false
            })

            // Invalidate admin queries
            queryClient.invalidateQueries({
                queryKey: ['admin-game-platforms'],
                exact: false
            })

            // Invalidate platform-specific queries
            queryClient.invalidateQueries({
                queryKey: ['platform-popular'],
                exact: false
            })
            queryClient.invalidateQueries({
                queryKey: ['platform-price-drops'],
                exact: false
            })
            queryClient.invalidateQueries({
                queryKey: ['platform-recent'],
                exact: false
            })

            // Invalidate price drops queries
            queryClient.invalidateQueries({
                queryKey: ['price-drops'],
                exact: false
            })

            // Invalidate featured games
            queryClient.invalidateQueries({
                queryKey: ['featured-games'],
                exact: false
            })

            notifications.show({
                title: 'Success',
                message: 'Platform image updated successfully',
                color: 'green'
            })

            setImageBrowserOpened(false)
        } catch (error) {
            console.error('Failed to update platform image:', error)
            notifications.show({
                title: 'Error',
                message: 'Failed to update platform image',
                color: 'red'
            })
        }
    }

    const handleOpenAddPriceModal = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setAddPriceModalOpened(true)
    }

    const handleCloseAddPriceModal = () => {
        setAddPriceModalOpened(false)
    }

    const handleEditGame = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        // Navigate to AdminGameEdit with the platform parameter to select the correct tab
        navigate(`/admin/games/${gamePlatform.gameId}/edit?platform=${gamePlatform.platform.slug}`)
    }

    const handleAmazonSearch = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        searchOnAmazon(gamePlatform.game.title)
    }

    // Defensive check for required properties
    if (!gamePlatform.game || !gamePlatform.platform) {
        console.warn('GameCard: Missing required game or platform data', gamePlatform);
        return null;
    }

    return (
        <>
            <Card
                key={`${gamePlatform.gameId}-${gamePlatform.platformId}`}
                component={Link}
                to={`/game/${gamePlatform.game.slug}/${gamePlatform.platform.slug}`}
                onClick={onClick}
                shadow="none"
                radius="md"
                withBorder={false}
                p={0}
                style={{
                    textDecoration: 'none',
                    transition: 'transform 0.2s ease',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    height: compact ? 'auto' : '340px', // Fixed height for consistent alignment
                    backgroundColor: 'transparent'
                }}
            >
                {/* Image section - will grow to fill available space */}
                <Card.Section p="md" style={{ position: 'relative', flex: 1, minHeight: 0, maxHeight: '280px' }}>
                    {imageUrl ? (
                        <Image
                            src={imageUrl}
                            alt={gamePlatform.game.title}
                            fit="contain"
                            radius="md"
                            style={{
                                height: '100%',
                                width: '100%',
                                backgroundColor: colorScheme === 'dark' ? 'var(--mantine-color-dark-5)' : '#f8f9fa'
                            }}
                            fallbackSrc="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE2MCIgdmlld0JveD0iMCAwIDMyMCAxNjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTYwIiBmaWxsPSIjRjFGM0Y0Ii8+CjxwYXRoIGQ9Ik0xNDQuNSA4MEwxNjAgOTVIMTQ0LjVWODBaIiBmaWxsPSIjOUM5RTlGIi8+CjxwYXRoIGQ9Ik0xNzUuNSA4MEwxOTEgOTVIMTc1LjVWODBaIiBmaWxsPSIjOUM5RTlGIi8+Cjx0ZXh0IHg9IjE2MCIgeT0iMTEwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNjE2MTYxIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiPk5vIEltYWdlPC90ZXh0Pgo8L3N2Zz4="
                        />
                    ) : (
                        <div
                            style={{
                                height: '100%',
                                backgroundColor: colorScheme === 'dark' ? 'var(--mantine-color-dark-4)' : '#F1F3F4',
                                border: `1px solid ${colorScheme === 'dark' ? 'var(--mantine-color-dark-3)' : '#e9ecef'}`,
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                gap: '8px'
                            }}
                        >
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                                    stroke={colorScheme === 'dark' ? 'var(--mantine-color-dark-2)' : '#9C9E9F'}
                                    strokeWidth="2"
                                    fill="none"
                                />
                                <circle cx="8.5" cy="8.5" r="1.5" fill={colorScheme === 'dark' ? 'var(--mantine-color-dark-2)' : '#9C9E9F'} />
                                <path
                                    d="m15 10-3 3-2-2-4 4h12V10z"
                                    fill={colorScheme === 'dark' ? 'var(--mantine-color-dark-2)' : '#9C9E9F'}
                                />
                            </svg>
                            <Text size="sm" c="dimmed">No Image</Text>
                        </div>
                    )}

                    {/* Badges container - positioned absolutely on the image */}
                    <div style={{
                        position: 'absolute',
                        top: compact ? 8 : 24,
                        right: compact ? 8 : 24,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        alignItems: 'flex-end'
                    }}>
                        {/* Price drop badge */}
                        {hasPriceDrop && dropPercentage > 0 && (
                            <Badge
                                color="green"
                                variant="filled"
                                size={compact ? "s" : "lg"}
                                style={{
                                    fontWeight: 700,
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                }}
                            >
                                -{dropPercentage.toFixed(0)}%
                            </Badge>
                        )}

                        {/* Rating badge */}
                        {gamePlatform.metacriticScore && (
                            <Badge
                                color={getRatingColor(gamePlatform.metacriticScore)}
                                variant="filled"
                                size={compact ? "sm" : "md"}
                                style={{
                                    fontWeight: 700,
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                }}
                            >
                                {gamePlatform.metacriticScore}
                            </Badge>
                        )}
                    </div>

                    {/* Admin controls - positioned at bottom center of image */}
                    {isAdmin && (
                        <div style={{
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            backdropFilter: 'blur(4px)',
                            position: 'absolute',
                            bottom: compact ? 8 : 40,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            display: 'flex',
                            gap: '6px',
                            padding: '8px 12px',
                            borderRadius: '8px',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            zIndex: 10,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                        }}>
                            <Tooltip label="Edit game platform">
                                <ActionIcon
                                    variant="filled"
                                    color="gray"
                                    size="md"
                                    onClick={handleEditGame}
                                    style={{
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                    }}
                                >
                                    <IconEdit size={14} />
                                </ActionIcon>
                            </Tooltip>

                            <Tooltip label="Change platform image">
                                <ActionIcon
                                    variant="filled"
                                    color="blue"
                                    size="md"
                                    onClick={handleOpenImageBrowser}
                                    style={{
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                    }}
                                >
                                    <IconPhoto size={14} />
                                </ActionIcon>
                            </Tooltip>

                            <Tooltip label="Search on Amazon">
                                <ActionIcon
                                    variant="filled"
                                    color="orange"
                                    size="md"
                                    onClick={handleAmazonSearch}
                                    style={{
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                    }}
                                >
                                    <Image
                                        src="https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg"
                                        alt="Amazon"
                                        w={14}
                                        h={14}
                                        fit="contain"
                                    />
                                </ActionIcon>
                            </Tooltip>

                            <Tooltip label="Add price">
                                <ActionIcon
                                    variant="filled"
                                    color="green"
                                    size="md"
                                    onClick={handleOpenAddPriceModal}
                                    style={{
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                    }}
                                >
                                    <IconPlus size={14} />
                                </ActionIcon>
                            </Tooltip>
                        </div>
                    )}

                    {/* Release date badge - positioned at bottom center of image (only if not admin or if admin but not compact) */}
                    {shouldShowReleaseDate && gamePlatform.releaseDate && (!isAdmin || !compact) && (
                        <div style={{
                            position: 'absolute',
                            bottom: compact ? 8 : 16,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            // removed right and width for centering
                        }}>
                            <Badge
                                color={isFuture ? "blue" : "green"}
                                variant="filled"
                                size={compact ? "sm" : "md"}
                                style={{
                                    fontWeight: 600,
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                }}
                            >
                                {formatReleaseDate(gamePlatform.releaseDate)}
                            </Badge>
                        </div>
                    )}
                </Card.Section>

                {/* Content section - title and price */}
                <div style={{ flexShrink: 0, marginBottom: 'auto', padding: '0 0' }}>
                    <Stack gap="xs">
                        {/* Game title */}
                        <Text
                            size={"lg"}
                            fw={400}
                            c={colorScheme === 'dark' ? 'customYellow' : 'darkLink'}
                            lineClamp={2}
                            title={gamePlatform.game.title}
                        >
                            {gamePlatform.game.title}
                        </Text>

                        {/* Price section - positioned below title */}
                        {lowestPrice && (
                            <Group gap="xs" align="baseline">
                                {/* Current price */}
                                <Text
                                    size={compact ? "sm" : "lg"}
                                    fw={700}
                                    c="customGreen"
                                    style={{ lineHeight: 1 }}
                                >
                                    {currentPrice
                                        ? formatPrice(currentPrice, lowestPrice.currency || 'USD')
                                        : 'Price unavailable'
                                    }
                                </Text>

                                {/* Show old price with strikethrough if there's a price drop */}
                                {hasPriceDrop && oldPrice && (
                                    <Text
                                        size={compact ? "xs" : "sm"}
                                        c="dimmed"
                                        td="line-through"
                                        style={{ lineHeight: 1 }}
                                    >
                                        {formatPrice(oldPrice, lowestPrice.currency || 'USD')}
                                    </Text>
                                )}
                            </Group>
                        )}

                        {showDescription && gamePlatform.game.description && (
                            <Text size="xs" c="dimmed" lineClamp={2}>
                                {gamePlatform.game.description}
                            </Text>
                        )}
                    </Stack>
                </div>
            </Card>

            {/* Admin Modals */}
            {isAdmin && imageBrowserOpened && (
                <ImageBrowserModal
                    opened={imageBrowserOpened}
                    onClose={handleCloseImageBrowser}
                    onSelect={handleImageSelect}
                    gameId={gamePlatform.gameId}
                    platformId={gamePlatform.platformId}
                    title={`Select Image for ${gamePlatform.game.title} (${gamePlatform.platform.name})`}
                />
            )}

            {isAdmin && addPriceModalOpened && (
                <AddPriceModal
                    opened={addPriceModalOpened}
                    onClose={handleCloseAddPriceModal}
                    preselectedGameId={gamePlatform.gameId}
                    preselectedPlatformId={gamePlatform.platformId}
                />
            )}
        </>
    )
} 